/**
 * "Lapsed trials (never bought)" — the PROSPECT list (v_lapsed_trial, 50 accounts, 175 seats).
 *
 * People who ran a Zoom trial, let it lapse, and never bought anything. That is all this list means.
 *
 * THE COPY IS THE FEATURE, AND HERE THE FEATURE IS RESTRAINT. This is a first-sale motion, not a fault
 * report: nothing is broken, nobody is owed anything, and no one has been lost. If it is dressed in the
 * red-and-warning language of the paid-expired list next to it, two things break at once — the CSM starts
 * treating a cold prospect as an emergency, and (far worse) they stop reading the red badges, which is
 * exactly when they will miss a paying customer Zoom wrongly expired. The two lists earn their own tones.
 *
 * NO MONEY COLUMNS. Not because they are stripped — because by definition there is none: every account
 * here has paid us exactly zero. The view carries no money at all, so there is nothing to gate.
 *
 * Sorted biggest-opportunity-first (seats trialled), then most-recently-lapsed — a 30-seat trial that ran
 * out last week is a warmer call than a 2-seat one from a year ago.
 *
 * Purpose-built (NOT the generic WorklistTable): there is no utilization here, and "trial ended" is not a
 * renewal date — WorklistTable's column probing would render the wrong things.
 */

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Table, type Column, ErrorState, EmptyState, SkeletonRows } from "@/components/ui";
import { formatDate, formatInt } from "@/lib/format";
import { planName } from "@/lib/planNames";
import type { LapsedTrialRow } from "@/api/types";
import { useLapsedTrial } from "./useWorklists";

export function LapsedTrialTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wl = useLapsedTrial();

  if (wl.isPending) return <SkeletonRows rows={4} />;
  if (wl.isError) return <ErrorState error={wl.error} onRetry={() => void wl.refetch()} />;

  const columns: Column<LapsedTrialRow>[] = [
    {
      key: "account",
      header: t("worklist.col.account"),
      cell: (r) => (
        <span className="font-medium text-(--text)">
          {r.account_name ?? r.account_number ?? r.account_id}
        </span>
      ),
      sortValue: (r) => r.account_name ?? r.account_number ?? r.account_id,
    },
    {
      key: "owner",
      // A first-sale list, so it carries what a salesperson actually dials.
      header: t("customer.owner"),
      cell: (r) => <span className="text-(--text-muted)">{r.owner_email ?? "—"}</span>,
      sortValue: (r) => r.owner_email,
    },
    {
      key: "plan",
      // The trial SKU they were on (e.g. zoent_freetrial) — it says WHAT they tried.
      header: t("customer.plan"),
      cell: (r) => planName(r.plan ?? r.base_type),
      sortValue: (r) => planName(r.plan ?? r.base_type),
    },
    {
      key: "seats_trialled",
      // The SIZE of the opportunity, and the view's primary sort. Not a current seat count — the plan is
      // dead — which is why the header says "trialled" and not "seats".
      header: t("worklist.lapsedTrial.seatsTrialled"),
      cell: (r) => (r.seats_trialled == null ? "—" : formatInt(r.seats_trialled)),
      sortValue: (r) => r.seats_trialled,
      align: "right",
      numeric: true,
    },
    {
      key: "lapsed_on",
      header: t("worklist.lapsedTrial.lapsedOn"),
      cell: (r) => (
        <span className="flex flex-col">
          <span>{formatDate(r.lapsed_on)}</span>
          {r.days_since_lapsed != null && (
            <span className="text-(length:--text-2xs) text-(--text-subtle)">
              {t("worklist.lapsedTrial.daysSince", { days: r.days_since_lapsed })}
            </span>
          )}
        </span>
      ),
      sortValue: (r) => r.lapsed_on,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {/* The framing note. Neutral panel, not a warn/danger one: this list is an OPPORTUNITY, and the
          visual language has to agree with the words or the words lose. */}
      <p className="rounded-(--radius-sm) border border-(--border) bg-(--surface-sunken) px-3 py-2 text-(length:--text-sm) text-(--text-muted)">
        {t("worklist.lapsedTrial.note")}
      </p>
      <Table
        columns={columns}
        rows={wl.data.rows}
        rowKey={(r) => r.account_id}
        caption={t("worklist.lapsedTrial.title")}
        onRowClick={(r) => navigate(`/customers/${encodeURIComponent(r.account_id)}`)}
        empty={
          <EmptyState
            title={t("worklist.lapsedTrial.emptyTitle")}
            body={t("worklist.lapsedTrial.emptyBody")}
          />
        }
      />
    </div>
  );
}
